import { Component, ElementRef, EventEmitter, Injector, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { SafeHtml, DomSanitizer } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Checklist } from 'src/app/components/checklists/checklists';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';
import { ChecklistStatusComponent } from '../../checklist-status/checklist-status.component';
import { WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { FormStatusEnum } from 'src/app/common/enumerations/enumerations';
import { YesNoDialogComponent } from '../../yes-no-dialog/yes-no-dialog.component';
import { TextareaDialogComponent } from '../../textarea-dialog/textarea-dialog.component';
import { DocumentChip } from '../../checklist-chips/document-chips.component';

@Component({
  selector: 'op-approval-w-checklists',
  templateUrl: './op-approval-w-checklists.component.html',
  styleUrls: ['./op-approval-w-checklists.component.scss']
})
export class OpApprovalWChecklistsComponent extends ClBaseComponent implements OnInit {

  @Input() privilegeToApproveAnswer?: PrivilegeEnum;
  @Input() privilegeWoApproval?: PrivilegeEnum;
  @Input() answerChangedById?: number;
  @Input() answerChangedBy?: User;
  @Input() answer?: boolean;
  @Input() approved?: boolean;
  @Input() rolesToApproveChanges?: string[];

  @Input() questionDisabled?: boolean = false;
  @Input() followUpChanged?: boolean;

  @Output() approveAnswer = new EventEmitter<any>();
  @Output() disapproveAnswer = new EventEmitter<any>();
  @Output() pendingOpApprovalChanges = new EventEmitter<OpApprovalValue>();

  @ViewChild(ChecklistStatusComponent) checklistStatus?: ChecklistStatusComponent;
  @ViewChild('container', { read: ElementRef }) container?: ElementRef;

  canApproveAnswer = false;

  name?: string | null;

  visible?: boolean = false;

  questionSanitized!: SafeHtml;
  lastApproval!: SafeHtml | null;
  lastChange!: SafeHtml | null;
  lastChangeBy!: SafeHtml | null;
  checklistChips?: DocumentChip[];

  checklistsIDs!: number[];
  prevChecklistsIDs!: number[];
  checklistTypeID!: number;
  checklistNotClosed!: boolean;

  checklists!: Checklist[];
  checklists$!: Observable<Checklist[]>;
  checklistsSubs!: Subscription;

  checkboxChanged = false;
  task!: WfTaskLocalResource;

  constructor(
    protected override injector: Injector,
    private sanitizer: DomSanitizer,
  ) {
    super(injector);
  }

  override ngOnDestroy(): void {
    this.checklistsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  ngOnInit() {
    this.setCanAnswer();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.loadChecklists();
    this.checklistTypeID = this.utils.JSONparse(this.configuration)?.ChecklistTypeID ?? 0;
    this.refresh();
  }

  loadChecklists() {
    this.checklists$ = this.store.select(state => state.Checklists.data);
    this.checklistsSubs = this.checklists$.subscribe(data => {
      if (data.length) {
        this.checklists = data;
      }
    });
  }

  /**
   * Rebuilds the checkbox, approve-button and change-history state from the stored op-approval value.
   * The OIC Approve button shows for a checked answer that was approved, auto-checked, or given while drafting.
   */
  refresh() {
    const opApprovalValueFromBE = this.utils.JSONparse(this.utils.getTextValue(this.valueString));
    const opApprovalValue = this.utils.convertObjectKeysToCamelCase<OpApprovalValue>(opApprovalValueFromBE) as OpApprovalValue;
    this.checked = opApprovalValue?.checked ?? this.answer;
    // Answers saved while drafting are initial answers, not changes awaiting approval; this also covers
    // forms already saved before draft answers were stored as approved.
    // formStatus must be present: FormStatusEnum.None and New share the value 0.
    const draftAnswer = opApprovalValue?.formStatus != null && [FormStatusEnum.New, FormStatusEnum.Draft, FormStatusEnum.AmendmentDrafting].includes(opApprovalValue.formStatus);
    const approved = opApprovalValue?.approved || opApprovalValue?.autoChecked || draftAnswer;
    this.questionSanitized = this.sanitizer.bypassSecurityTrustHtml(this.question ?? '');
    if (this.showApprove) {
      this.label = 'Approve';
      this.name = null;
      this.date = null;
      this.color = 'primary';
      this.visible = this.checked && approved;
    } else if (this.showUnapprove) {
      this.label = 'Unapprove';
      this.color = 'accent';
      this.visible = this.checked;
      this.name = this.user?.name;
    }
    this.setCanAnswer();
    let checklists: Checklist[] = [];
    this.checklistsIDs = opApprovalValue?.checklistIds;
    checklists = this.checklists?.filter(c => this.checklistsIDs?.includes(c.id));

    this.checklistNotClosed = (this.checked && this.checklistTypeID && !checklists.length) || checklists?.some(c => ![FormStatusEnum.Closed].includes(c.checklistStatus?.formStatus.id ?? 0));

    if (!this.checklistsIDs) this.checklistsIDs = [];
    this.lastApproval = this.getSafeContent(this.signature?.lastFollowUpChanged ?? '');
    const checklistChipsData = this.signature?.lastFollowUpCheckChanged?.split("|");
    if (checklistChipsData?.length) {
      this.lastChange = this.getSafeContent(checklistChipsData[0]);
      this.lastChangeBy = this.getSafeContent(checklistChipsData[1]);
      this.checklistChips = this.utils.JSONparse(checklistChipsData[2]) ?? checklistChipsData[2];
    }
  }

  setCanAnswer() {
    if (this.answerChangedById && this.answerChangedById !== this.currentUser?.id && this.signature?.pendingChange) {
      this.questionDisabled = true;
      this.canApproveAnswer = this.hasPrivilege(this.privilegeToApproveAnswer as PrivilegeEnum);
    }
    else {
      this.canApproveAnswer = false;
    }
  }

  checkboxChange() {
    if (!this.checkboxChanged || this.checked) {
      const confirm = this.dialog.open(YesNoDialogComponent, {
        width: '400px',
        data: {
          message: environment.messages.answer_message
        }
      });
      confirm.afterClosed().toPromise().then(data => {
        if (data) {
          this.setFormDirty();
          // uncheck box and remove checklists
          if (!this.checked || this.checked && (this.checklistsIDs.length || !this.checklistTypeID)) {
            this.checklistsIDs = this.checked ? this.checklistsIDs : [];
            this.valuesChanged();
            this.answer = this.checked;
          }
          // check box
          else if (this.checklistTypeID && !this.checklistsIDs.length) {
            if (this.status != FormStatusEnum.Draft && this.status != FormStatusEnum.New) {
              this.checkboxChanged = true;
            }
            else {
              this.valuesChanged();
            }
            if (this.checklistStatus)
              this.checklistStatus.checkboxChanged = true;
          }
          // this.valuesChanged();
        } else {
          this.checked = !this.checked;
          this.valuesChanged();
        }
      });
    }
  }

  textChanged(value: OpApprovalValue) {
    this.checklistsIDs = value.checklistIds;
    this.approved = value.approved;
    if (this.checked)
      this.valuesChanged();
  }

  valuesChanged() {
    this.checkboxChanged = false;
    const value = { checked: this.checked, checklistIds: this.checklistsIDs, approved: this.approved, user: this.currentUser?.id };
    this.valueString = this.utils.JSONstringify(value);
    this.changed.emit(this.valueString);
    this.setFormDirty(false);
  }

  click() {
    if (!this.approved) {
      const confirm = this.dialog.open(YesNoDialogComponent, {
        width: '400px',
        data: {
          message: this.getMessage('approve_message')?.description
        }
      });

      confirm.afterClosed().subscribe(data => {
        if (data) {
          this.name = this.currentUser?.name;
          this.date = new Date();

          this.approve.emit({
            value: !this.approved,
            user: !this.approved ? this.currentUser?.id : 0,
            date: this.date,
          });
        }
      });
    } else {
      const confirm = this.dialog.open(YesNoDialogComponent, {
        width: '400px',
        data: {
          message: this.getMessage('unapprove_message')?.description
        }
      });
      confirm.afterClosed().subscribe(data => {
        if (data) {
          this.name = null;
          this.date = null;
          this.unapprove.emit({
            value: !this.approved,
            user: !this.approved ? this.currentUser?.id : 0,
            date: this.date
          });
        }
      });
    }
  }

  apprAns() {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: environment.messages.approve_message
      }
    });
    confirm.afterClosed().subscribe(data => {
      if (data) {
        this.approveAnswer.emit();
      }
    });
  }

  disapAns() {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: environment.messages.disapprove_message_no_reason
      }
    });
    confirm.afterClosed().subscribe(data => {
      if (data) {
        this.disapproveAnswer.emit();
      }
    });
  }

  disap() {
    const confirm = this.dialog.open(TextareaDialogComponent, {
      width: '400px',
      data: {
        label: 'Dissaprove reason',
        text: ''
      }
    });
    confirm.afterClosed().subscribe(data => {
      if (data.acceptClicked) {
        this.name = null;
        this.date = null;

        this.disapprove.emit({
          value: !this.approved,
          user: !this.approved ? this.currentUser?.id : 0,
          date: this.date,
          text: data.text
        });
      }
    });
  }

  undo() {
    this.disapproveAnswer.emit();
  }

  getSafeContent(s: string) {
    if (!s) return null;
    return this.sanitizer.bypassSecurityTrustHtml(s);
  }

  pendingOpApprovalChangesEvent(e: OpApprovalValue) {
    e.taskID = this.signature?.id;
    e.changedByID = this.currentUser?.id ?? 0;
    e.changedOn = new Date().toISOString();
    e.checked = this.checked ?? false;
    this.pendingOpApprovalChanges.emit(e);
  }
}

export interface OpApprovalValue {
  checked: boolean;
  checklistIds: number[];
  changedByID: number;
  changedOn: string;
  approved: boolean;
  approvedByID: number;
  approvedOn: string;
  rejectedByID: number;
  rejectedOn: string;
  taskID?: number;
  formStatus?: FormStatusEnum;
  autoChecked?: boolean;
  user?: number;
}
